import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Column } from '@ant-design/plots'
import { Col, Row, Skeleton } from 'antd'
import { formatMoney } from 'common'

import { getBenefitUsageByMonth, getBenefitUsageOverview } from 'core'

import { Card, Icon, Tooltip } from 'common/components'
import { useQueryRequestV2 } from 'common/hooks'

//
import './style.less'

type AllowancesStatisticsProps = { planPeriodId: string }

function AllowancesStatistics({ planPeriodId }: AllowancesStatisticsProps) {
  const { t: translate } = useTranslation()
  const {
    data: benefitUsageOverview,
    isLoading: isBenefitUsageOverviewLoading,
    sendQuery: sendGetBenefitUsageOverview,
  } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getBenefitUsageOverview(planPeriodId),
  })
  const {
    data: benefitUsageByMonth,
    isLoading: isBenefitUsageByMonthLoading,
    sendQuery: sendGetBenefitUsageByMonth,
  } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getBenefitUsageByMonth(planPeriodId),
  })

  useEffect(() => {
    ;(async () => {
      if (planPeriodId) {
        await sendGetBenefitUsageOverview(() => getBenefitUsageOverview(planPeriodId))
        await sendGetBenefitUsageByMonth(() => getBenefitUsageByMonth(planPeriodId))
      }
    })()
  }, [planPeriodId])

  return (
    <Card className={'t-benefit-usage-overtime h-100'}>
      <Skeleton loading={isBenefitUsageOverviewLoading || isBenefitUsageByMonthLoading}>
        <div className="flex flex__column flex__space_between h-100">
          {' '}
          <div className="allowances-statistics-container">
            <p className="allowances-statistics_header mb-24">
              {translate('ee-dashboard.home.statistics.benefit-usage-title')}
            </p>
            <div className="allowances-statistics_card mb-32">
              {benefitUsageOverview?.totalAllowance === 0 && benefitUsageOverview?.totalReimbursed === 0 ? (
                <div className="text-center" style={{ margin: 'auto' }}>
                  <div className="allowances-statistics_card-title">
                    <span className="allowances-statistics_card-zero-state-text semi-bold">
                      No data available yet...
                    </span>
                  </div>
                  <span className="allowances-statistics_card-zero-state-paragraph">
                    This data will be available after your benefit starts.
                  </span>
                </div>
              ) : (
                <Row className="flex flex__space_between">
                  <div className="allowances-statistics_card-item w-auto m-4">
                    <div className="allowances-statistics_card-title">
                      <span className="mr-4 ">{translate('ee-dashboard.home.statistics.total-allowance-label')}</span>
                      <Tooltip
                        title={translate('ee-dashboard.home.statistics.total-allowance-tooltip')}
                        position="bottom"
                      >
                        <Icon type="far fa-info-circle" className="info_icon" fontAwesome />
                      </Tooltip>
                    </div>
                    <span className="allowances-statistics_card-value-green t-totalAllowance">
                      {formatMoney(benefitUsageOverview?.totalAllowance)}
                    </span>
                  </div>
                  <div className="allowances-statistics_card-item w-auto m-4">
                    <div className="allowances-statistics_card-title">
                      <span className="mr-4">Monthly allowance</span>
                      <Tooltip title="This is the total allowance you have received for this month" position="bottom">
                        <Icon type="far fa-info-circle" className="info_icon" fontAwesome />
                      </Tooltip>
                    </div>
                    <span className="allowances-statistics_card-value t-totalReimbursed">
                      {formatMoney(benefitUsageOverview?.monthlyAllowance)}
                    </span>
                  </div>
                  <div className="allowances-statistics_card-item w-auto m-4">
                    <div className="allowances-statistics_card-title">
                      <span className="mr-4">
                        {translate('ee-dashboard.home.statistics.total-reimbursements-label')}
                      </span>
                      <Tooltip
                        title={translate('ee-dashboard.home.statistics.total-reimbursements-tooltip')}
                        position="bottom"
                      >
                        <Icon type="far fa-info-circle" className="info_icon" fontAwesome />
                      </Tooltip>
                    </div>
                    <span className="allowances-statistics_card-value t-totalReimbursed">
                      {formatMoney(benefitUsageOverview?.totalReimbursed)}
                    </span>
                  </div>
                </Row>
              )}
            </div>
            <div className="allowances-statistics_legend">
              <span className="allowances-statistics_legend-item allowance-used">
                {translate('ee-dashboard.home.statistics.allowance-used')}
              </span>
            </div>
          </div>
          {benefitUsageByMonth && (
            <Column
              data={benefitUsageByMonth}
              xField="monthShort"
              yField="reimbursed"
              meta={{
                reimbursed: {
                  alias: 'Reimbursed',
                  formatter: (value: number) => `$${value}`,
                },
              }}
              label={{
                position: 'middle',
                style: {
                  fill: '#FFFFFF',
                  opacity: 0.6,
                },
              }}
              xAxis={{
                label: {
                  autoRotate: false,
                },
              }}
              columnStyle={{
                fill: '#11D175',
              }}
              legend={false}
              height={267}
            />
          )}
        </div>
      </Skeleton>
    </Card>
  )
}

export default AllowancesStatistics
